/**
 * Lightweight progress bar controller (singleton).
 * Tracks active async work (axios requests, route transitions).
 * Ramps quickly to ~80%, trickles slowly, finishes only when all work is done.
 */

let progress = 0;
let active = 0;
let trickleTimer = null;
let listeners = new Set();

function emit() {
  listeners.forEach((fn) => fn(progress));
}

function trickle() {
  clearInterval(trickleTimer);
  trickleTimer = setInterval(() => {
    if (progress >= 0.95) {
      clearInterval(trickleTimer);
      return;
    }
    // Slow down as we approach 1.0
    const step = progress < 0.7 ? 0.08 : progress < 0.85 ? 0.02 : 0.005;
    progress = Math.min(progress + step, 0.95);
    emit();
  }, 200);
}

const progressBar = {
  /** Subscribe to progress changes. Returns unsubscribe fn. */
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  /** Signal that an async operation has started. */
  start() {
    active += 1;
    if (active === 1) {
      progress = 0.05;
      emit();
      trickle();
    }
  },

  /** Signal that an async operation has finished. */
  done() {
    active = Math.max(0, active - 1);
    if (active === 0) {
      clearInterval(trickleTimer);
      progress = 1;
      emit();
      // Reset after the finish animation
      setTimeout(() => {
        progress = 0;
        emit();
      }, 350);
    }
  },

  /** Force-finish the bar (e.g. on error). */
  finish() {
    active = 0;
    clearInterval(trickleTimer);
    progress = 1;
    emit();
    setTimeout(() => {
      progress = 0;
      emit();
    }, 350);
  },

  /** Get current value. */
  get() {
    return progress;
  }
};

export default progressBar;
